import type { VisualizationModel } from "./visualization-model";

interface EquityCurveInput {
  id: string;
  title: string;
  lineColor: string;
  points: Array<{ timeMs: number; value: number }>;
  minY: number;
  maxY: number;
  domainStartMs: number;
  domainEndMs: number;
  timeScaleLabel: string;
}

export function buildEquityCurveVisualization(
  input: EquityCurveInput,
): VisualizationModel {
  return {
    id: input.id,
    title: input.title,
    family: "area",
    data: input.points.map((point) => ({
      date: new Date(point.timeMs),
      pnl: point.value,
    })),
    series: [
      {
        key: "pnl",
        label: "Profit",
        family: "area",
        axis: "left",
        format: "USD",
        colorToken: input.lineColor,
      },
    ],
    xAxis: {
      key: "date",
      label: "Time",
      format: "date-time",
      domain: [
        new Date(input.domainStartMs).toISOString(),
        new Date(input.domainEndMs).toISOString(),
      ],
    },
    yAxes: [
      {
        id: "left",
        label: "Profit",
        format: "USD",
        domain: [input.minY, input.maxY],
      },
    ],
    accessibleSummary: `Auto-scaled performance chart, ${input.timeScaleLabel}`,
    updatedAt: null,
    state: "ready",
  };
}

interface MacroLineInput {
  id: string;
  title: string;
  color: string;
  points: Array<{ date: string | null; value: number }>;
}

export function buildMacroLineVisualization(
  input: MacroLineInput,
): VisualizationModel {
  const data = input.points.map((point, index) => ({
    date: new Date(resolveTimestamp(point.date, index)),
    actual: point.value,
  }));
  const values = input.points.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max(
    (max - min) * 0.08,
    Math.abs(max || min) * 0.01,
    0.01,
  );
  const firstDate = data[0]?.date as Date | undefined;
  const lastDate = data.at(-1)?.date as Date | undefined;
  const domain = exactDateDomain(firstDate, lastDate);

  return {
    id: input.id,
    title: input.title,
    family: "line",
    data,
    series: [
      {
        key: "actual",
        label: "Actual",
        family: "line",
        axis: "left",
        format: "macro actual",
        colorToken: input.color,
      },
    ],
    xAxis: {
      key: "date",
      label: "Print date",
      format: "date",
      domain,
    },
    yAxes: [
      {
        id: "left",
        label: "Actual",
        format: "macro actual",
        domain: [min - padding, max + padding],
      },
    ],
    accessibleSummary: `${input.title} actual value history`,
    updatedAt: null,
    state: "ready",
  };
}

interface ReactionLineInput {
  id: string;
  title: string;
  series: Array<{
    label: string;
    color: string;
    points: Array<{ time: number; close: number }>;
  }>;
}

export function buildReactionLineVisualization(
  input: ReactionLineInput,
): VisualizationModel {
  const rows = new Map<number, Record<string, unknown>>();
  const series = input.series.map((item, seriesIndex) => {
    const key = `reaction${seriesIndex}`;
    const rawKey = `${key}Raw`;
    const values = item.points.map((point) => point.close);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    for (const point of item.points) {
      const timeMs = epochMilliseconds(point.time);
      const row = rows.get(timeMs) ?? { date: new Date(timeMs) };
      row[key] = (point.close - min) / span;
      row[rawKey] = point.close;
      rows.set(timeMs, row);
    }
    return {
      key,
      label: item.label,
      family: "line" as const,
      axis: "left" as const,
      format: "relative move",
      colorToken: item.color,
    };
  });
  const data = [...rows.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, row]) => row);
  const firstDate = data[0]?.date as Date | undefined;
  const lastDate = data.at(-1)?.date as Date | undefined;

  return {
    id: input.id,
    title: input.title,
    family: "line",
    data,
    series,
    xAxis: {
      key: "date",
      label: "Five-minute bar",
      format: "date-time",
      domain: exactDateDomain(firstDate, lastDate),
    },
    yAxes: [
      {
        id: "left",
        label: "Relative move",
        format: "normalized",
        domain: [0, 1],
      },
    ],
    accessibleSummary: `${input.title} normalized five-minute reaction history`,
    updatedAt: null,
    state: "ready",
  };
}

function resolveTimestamp(value: string | null, index: number) {
  const timestamp = Date.parse(value ?? "");
  return Number.isFinite(timestamp) ? timestamp : index * 60_000;
}

function epochMilliseconds(value: number) {
  return Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
}

function exactDateDomain(start?: Date, end?: Date): [string, string] {
  const startMs = start?.getTime() ?? 0;
  const rawEndMs = end?.getTime() ?? startMs;
  const endMs = rawEndMs > startMs ? rawEndMs : startMs + 60_000;
  return [new Date(startMs).toISOString(), new Date(endMs).toISOString()];
}
